import React, { useCallback, useEffect, useRef } from 'react'
import { useReactFlow } from '@xyflow/react'
import { NODE_DIMENSIONS } from '@genieartinc/schemas'
import { workflowInputs } from '@/components/studio/outputs'
import { CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { useShaderPreview } from '@/components/canvas/hooks/useShaderPreview'
import { useShaderGeneration } from '@/hooks/useShaderGeneration'
import { openShaderCodeEditor } from '@/components/canvas/shader/open-code-editor'
import { ShaderAudioControls } from '@/components/canvas/shader/ShaderAudioControls'
import { ShaderNodeAuthoring } from '@/components/canvas/shader/ShaderNodeAuthoring'
import { ShaderPreviewOverlays } from '@/components/canvas/shader/ShaderPreviewOverlays'
import { ShaderPromptComposer } from '@/components/canvas/shader/ShaderPromptComposer'
import { requestCloseShaderLibrary } from '@/components/canvas/shader/library-focus'
import { restoreShaderRevision } from '@/components/canvas/shader/generation-state'
import { describeShaderProvider, resolveShaderProviderConfig } from '@/components/canvas/shader/node-provider'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import type { ShaderAuthoringState, ShaderPromptEntry } from '@/lib/canvas/shader/authoring/types'
import type { ShaderInputSources, ShaderInputType } from '@/lib/canvas/shader/media/contract'
import type { RuntimeMode, ExtractedParam } from '@/lib/canvas/shader/types'

interface ShaderNodeProps {
  id: string
  selected?: boolean
  state?: Record<string, unknown>
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  zoom?: number
  width?: number
  height?: number
  /** Media delivered by upstream nodes, per input (resolved in ShaderNodeNode). */
  inputs?: ShaderInputSources
  /** Inputs that have an edge, whether or not their media has arrived. */
  connectedInputs?: ShaderInputType[]
}

const EMPTY: Record<string, unknown> = {}
const NO_INPUTS: ShaderInputSources = {}
const NO_TYPES: ShaderInputType[] = []
const BUTTON_CLASS = 'h-7 gap-1.5 rounded-md px-3 text-[11px]'

function ShaderNodeComponent({
  id,
  selected = false,
  state = EMPTY,
  onStateChange,
  onSelect,
  onHoverChange,
  width = NODE_DIMENSIONS.ShaderNode.width,
  inputs = NO_INPUTS,
  connectedInputs = NO_TYPES,
}: ShaderNodeProps) {
  const flow = useReactFlow()
  const cardRef = useRef<HTMLDivElement>(null)
  const captureMeta = useRef<Record<string, unknown>>({})

  const title = typeof state.title === 'string' && state.title ? state.title : 'Shader'
  const code = typeof state.code === 'string' ? state.code : ''
  const runtimeMode = (state.runtimeMode as RuntimeMode | undefined) ?? 'glsl'
  const paused = state.previewPaused === true
  const paramValues = (state.paramValues as Record<string, unknown> | undefined) ?? EMPTY
  const generationState = state.generation as ShaderAuthoringState['generation']
  const hasCode = code.trim().length > 0
  const history = Array.isArray(state.promptHistory) ? (state.promptHistory as ShaderPromptEntry[]) : []
  const wasSelected = useRef(selected)
  useEffect(() => {
    if (selected && !wasSelected.current) requestCloseShaderLibrary()
    wasSelected.current = selected
  }, [selected])

  // Captures are history entries (newest last) that feed connected nodes and Frame Output.
  const handleCaptured = useCallback((dataUrl: string) => {
    onStateChange?.((prev) => ({
      ...prev,
      createdItems: [
        ...(Array.isArray(prev.createdItems) ? prev.createdItems : []),
        { id: crypto.randomUUID(), type: 'image', content: dataUrl, metadata: { name: 'Shader capture', createdAt: new Date(), ...captureMeta.current } },
      ],
    }))
  }, [onStateChange])

  const preview = useShaderPreview({ code, runtimeMode, paused, paramValues, inputs, onCapture: handleCaptured })
  const { error, restart, capture } = preview
  const unresponsive = error?.kind === 'unresponsive'
  const blockingError = error && !unresponsive ? error : null

  const generation = useShaderGeneration(id, state, onStateChange, connectedInputs)
  // Read from node state, not the hook: an undo that takes the error back must take the message with it.
  const failure = generationState?.status === 'error'
    ? { message: generationState.error ?? '', code: generationState.code ?? 'UNKNOWN' }
    : null

  const handleParamChange = useCallback((name: string, value: number | string | boolean) => {
    onStateChange?.((prev) => ({ ...prev, paramValues: { ...((prev.paramValues as Record<string, unknown> | undefined) ?? {}), [name]: value } }))
  }, [onStateChange])

  const handleRestore = useCallback((entry: ShaderPromptEntry) => {
    onStateChange?.((prev) => restoreShaderRevision(prev, entry))
  }, [onStateChange])

  const handleDraftCommit = useCallback((prompt: string) => {
    onStateChange?.((prev) => ({ ...prev, prompt }))
  }, [onStateChange])

  const handleTogglePause = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onStateChange?.((prev) => ({ ...prev, previewPaused: !prev.previewPaused }))
  }, [onStateChange])

  const handleRestart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    restart()
  }, [restart])

  const handleCapture = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    captureMeta.current = {
      studioInputs: workflowInputs(flow.getNodes(), flow.getEdges(), id),
      settings: structuredClone({ code, runtimeMode, paramValues }),
    }
    capture()
  }, [flow, id, code, runtimeMode, paramValues, capture])

  const openCode = useCallback(() => openShaderCodeEditor(id), [id])
  const handleEditCode = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    openCode()
  }, [openCode])

  return (
    <NodeContainer
      nodeId={id}
      title={title}
      icon="zap"
      selected={selected}
      nodeType="shader"
      width={width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
      cardRef={cardRef}
    >
      <CardContent className="flex flex-col gap-2 p-2">
        {/* The aspect ratio follows the input, so it can't be a static Tailwind class. */}
        <div
          className="relative w-full overflow-hidden rounded-md border border-border bg-zinc-950"
          style={{ aspectRatio: `${preview.dims.w} / ${preview.dims.h}` }}
        >
          <div ref={preview.containerRef} className="absolute inset-0" aria-hidden={!hasCode} />
          <ShaderPreviewOverlays
            hasCode={hasCode}
            ready={preview.ready}
            blockingError={blockingError}
            unresponsiveMessage={unresponsive ? error?.message ?? '' : undefined}
            paused={paused}
            inputPreparing={preview.inputPreparing}
            justCaptured={preview.justCaptured}
            onWriteCode={openCode}
          />
        </div>

        <ShaderPromptComposer
          nodeId={id}
          initialPrompt={typeof state.prompt === 'string' ? state.prompt : ''}
          hasCode={hasCode}
          connectedInputs={connectedInputs}
          running={generation.running}
          progress={generation.progress}
          failure={generation.running ? null : failure}
          summary={generationState?.summary}
          completed={generation.completed}
          providerLabel={describeShaderProvider(resolveShaderProviderConfig(state.provider))}
          onSubmit={generation.run}
          onCancel={generation.cancel}
          onDraftCommit={handleDraftCommit}
        />

        <ShaderNodeAuthoring
          hasCode={hasCode}
          code={code}
          extractedParams={Array.isArray(state.extractedParams) ? state.extractedParams as ExtractedParam[] : undefined}
          paramValues={paramValues}
          history={history}
          onParamChange={handleParamChange}
          onReusePrompt={handleDraftCommit}
          onRestore={handleRestore}
        />

        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={handleTogglePause} disabled={!hasCode} className={BUTTON_CLASS} aria-label={paused ? 'Resume shader' : 'Pause shader'}>
            <Icon name={paused ? 'play' : 'pause'} className="h-3.5 w-3.5" />
            {paused ? 'Play' : 'Pause'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleRestart} disabled={!hasCode} className={BUTTON_CLASS} aria-label="Restart shader">
            <Icon name="rotate-ccw" className="h-3.5 w-3.5" />
            Restart
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCapture}
            disabled={!hasCode || !preview.ready || preview.inputPreparing || !!blockingError}
            className={BUTTON_CLASS}
            aria-label="Capture frame"
          >
            <Icon name="camera" className="h-3.5 w-3.5" />
            Capture
          </Button>
          <div className="ml-auto flex items-center gap-1.5">
            <ShaderAudioControls audio={preview.audio} />
            <Button variant="outline" size="sm" onClick={handleEditCode} className="h-7 w-7 rounded-md p-0" aria-label="Edit shader code" title="Edit code">
              <Icon name="code" className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </NodeContainer>
  )
}

export const ShaderNode = withNodeOptimization(ShaderNodeComponent, {
  compareProps: ['id', 'selected', 'state', 'width', 'zoom', 'inputs', 'connectedInputs'],
})
